import { useCallback, useMemo } from "react";
import {
  Stack,
  Text,
  Button,
  Group,
  Select,
  NumberInput,
  SegmentedControl,
  Alert,
  Divider,
  Modal,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@tabler/icons-react";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addMapping } from "../../../services/prefixService";
import { practicesService } from "../../../services/admin/practices.service";
import { surgeryCentersLabsService } from "../../../services/admin/surgeryCentersLabs.service";
import { physiciansService } from "../../../services/admin/physicians.service";

type MappingTargetType = "" | "practice" | "surgery_center" | "physician";

interface AddMappingFormValues {
  targetType: MappingTargetType;
  entityId: string;
  priority: number | string;
}

interface AddMappingFormProps {
  prefixId: string;
  onClose: () => void;
}

const TYPE_OPTIONS = [
  { label: "Practice", value: "practice" },
  { label: "Surgery Center", value: "surgery_center" },
  { label: "Physician", value: "physician" },
];

function classifyMappingError(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 409) {
      return "A mapping for this target already exists on this prefix.";
    }
    if (error.response?.status === 400) {
      return "Some details are missing or invalid. Check the form and try again.";
    }
    if (error.response?.status === 403) {
      return "You don't have permission to add mappings.";
    }
  }
  return "Unable to add mapping. Please try again.";
}

export default function AddMappingForm({
  prefixId,
  onClose,
}: AddMappingFormProps) {
  const queryClient = useQueryClient();

  const form = useForm<AddMappingFormValues>({
    initialValues: { targetType: "", entityId: "", priority: 100 },
    validate: {
      targetType: (value) => (value ? null : "Choose a type"),
      entityId: (value) => (value ? null : "Choose a target"),
    },
  });

  const { data: practices = [] } = useQuery({
    queryKey: ["practices"],
    queryFn: () => practicesService.getAll(),
  });

  const { data: surgeryCenters = [] } = useQuery({
    queryKey: ["surgeryCentersLabs"],
    queryFn: () => surgeryCentersLabsService.getAll(),
  });

  const { data: physicians = [] } = useQuery({
    queryKey: ["physicians"],
    queryFn: () => physiciansService.getAll(),
  });

  const practiceOptions = useMemo(
    () => practices.map((p) => ({ value: p.id, label: p.name })),
    [practices]
  );

  const scOptions = useMemo(
    () => surgeryCenters.map((sc) => ({ value: sc.id, label: sc.name })),
    [surgeryCenters]
  );

  const physicianOptions = useMemo(
    () =>
      physicians
        .filter((p) => p.isActive)
        .map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` })),
    [physicians]
  );

  const entityConfig = useMemo(() => {
    switch (form.values.targetType) {
      case "practice":
        return {
          label: "Practice",
          placeholder: "Select practice",
          options: practiceOptions,
        };
      case "surgery_center":
        return {
          label: "Surgery Center / Lab",
          placeholder: "Select surgery center",
          options: scOptions,
        };
      case "physician":
        return {
          label: "Physician",
          placeholder: "Select physician",
          options: physicianOptions,
        };
      default:
        return { label: "", placeholder: "", options: [] };
    }
  }, [form.values.targetType, practiceOptions, scOptions, physicianOptions]);

  const addMutation = useMutation({
    mutationFn: (values: AddMappingFormValues) =>
      addMapping(prefixId, {
        practiceId:
          values.targetType === "practice" ? values.entityId : undefined,
        surgeryCenterLabId:
          values.targetType === "surgery_center" ? values.entityId : undefined,
        physicianId:
          values.targetType === "physician" ? values.entityId : undefined,
        priority: typeof values.priority === "number" ? values.priority : 100,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prefix", prefixId] });
      notifications.show({ color: "green", message: "Mapping added" });
      onClose();
    },
    onError: (error) => {
      notifications.show({
        color: "red",
        message: classifyMappingError(error),
      });
    },
  });

  const handleTypeChange = useCallback(
    (value: string) => {
      form.setFieldValue("targetType", value as MappingTargetType);
      // Switching type discards any target picked for the previous type.
      form.setFieldValue("entityId", "");
    },
    [form]
  );

  const handleEntityChange = useCallback(
    (value: string | null) => {
      form.setFieldValue("entityId", value ?? "");
    },
    [form]
  );

  const handleSubmit = useCallback(
    (values: AddMappingFormValues) => {
      addMutation.mutate(values);
    },
    [addMutation]
  );

  const canSubmit = useMemo(
    () => Boolean(form.values.targetType && form.values.entityId),
    [form.values.targetType, form.values.entityId]
  );

  const errorMessage = useMemo(
    () => (addMutation.error ? classifyMappingError(addMutation.error) : null),
    [addMutation.error]
  );

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Modal.Body style={{ flex: 1, overflow: "auto" }}>
        <Stack gap="md">
          <SegmentedControl
            fullWidth
            data={TYPE_OPTIONS}
            value={form.values.targetType}
            onChange={handleTypeChange}
          />

          {form.values.targetType ? (
            <>
              <Select
                label={entityConfig.label}
                placeholder={entityConfig.placeholder}
                data={entityConfig.options}
                value={form.values.entityId || null}
                onChange={handleEntityChange}
                searchable
                clearable
                error={form.errors.entityId}
              />
              <NumberInput
                label="Priority"
                min={1}
                {...form.getInputProps("priority")}
              />
            </>
          ) : (
            <Text size="sm" c="dimmed">
              Choose a type to continue.
            </Text>
          )}

          {errorMessage && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              title="Couldn't add mapping"
              role="alert"
            >
              {errorMessage}
            </Alert>
          )}
        </Stack>
      </Modal.Body>

      <Divider />
      <Group justify="space-between" p="md">
        <Button variant="default" type="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          loading={addMutation.isPending}
          disabled={!canSubmit}
        >
          Add Mapping
        </Button>
      </Group>
    </form>
  );
}
